import { resolveCalendarLocale } from "./localeWeek";

/** The three parts of a date collected by separate inputs. */
export type DateField = "day" | "month" | "year";

/** Order used when the locale's date format can't be read. */
export const DEFAULT_DATE_FIELD_ORDER: readonly DateField[] = ["day", "month", "year"];

const DATE_FIELDS: readonly string[] = DEFAULT_DATE_FIELD_ORDER;
const REGION_DATE_FIELD_ORDERS: Readonly<Record<string, readonly DateField[]>> = {
  JP: ["year", "month", "day"],
};

/** A January date, used to format month names without depending on the current date. */
const REFERENCE_YEAR_UTC = 2024;
const MONTHS_PER_YEAR = 12;

function isDateField(type: string): type is DateField {
  return DATE_FIELDS.includes(type);
}

/**
 * The order the locale writes day, month and year in (e.g. day/month/year for
 * en-GB, month/day/year for en-US, year/month/day for ja).
 *
 * Falls back to day/month/year when the locale formats a date in some other
 * way or the browser has no `Intl` data for it.
 */
export function getDateFieldOrder(locale?: string): readonly DateField[] {
  const resolved = resolveCalendarLocale(locale);
  try {
    const region = resolved ? new Intl.Locale(resolved).region : undefined;
    const regionalOrder = region ? REGION_DATE_FIELD_ORDERS[region] : undefined;
    if (regionalOrder) return regionalOrder;

    const parts = new Intl.DateTimeFormat(resolved, {
      day: "numeric",
      month: "numeric",
      year: "numeric",
      timeZone: "UTC",
    }).formatToParts(new Date(Date.UTC(REFERENCE_YEAR_UTC, 0, 1)));

    const order = parts.map((part) => part.type).filter(isDateField);
    return new Set(order).size === DATE_FIELDS.length ? order : DEFAULT_DATE_FIELD_ORDER;
  } catch {
    return DEFAULT_DATE_FIELD_ORDER;
  }
}

/**
 * Abbreviated month names in the locale's own language, indexed from January
 * (e.g. "Sept", "sept.", "9月"), or `undefined` when the browser has no `Intl`
 * data for the locale and the caller should keep its own labels.
 */
export function getShortMonthNames(locale?: string): string[] | undefined {
  const resolved = resolveCalendarLocale(locale);
  try {
    const format = new Intl.DateTimeFormat(resolved, { month: "short", timeZone: "UTC" });
    return Array.from({ length: MONTHS_PER_YEAR }, (_, month) =>
      format.format(new Date(Date.UTC(REFERENCE_YEAR_UTC, month, 1))),
    );
  } catch {
    return undefined;
  }
}
